import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AccountMember, AccountMembersOverview } from 'src/app/models/AccountMember';
import { AccountMemberService, accountMemberErrorMessage } from 'src/app/shared/services/account-member.service';
import { toastOwnError } from 'src/app/shared/interceptors/http-error.interceptor';
import { AccountVersionService } from 'src/app/shared/services/account-version.service';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Multiusuários no header (CU-17tm466h1cm — telas 1, 2 e 7, sem cargos).
 *
 * Mesmo molde do `app-wallet-modal`: o item vive no dropdown do header, o modal
 * mora dentro dele e a abertura é um `Subject` empurrado pelo pai. O convite é
 * um segundo `ng-template` empilhado por cima da lista.
 *
 * Só o dono abre: para o membro o header nem mostra o item. Esconder é só
 * usabilidade: quem barra é o backend (`owner_only`).
 *
 * Cada mudança relê a lista e avisa o header (`overviewLoaded`), que mostra a
 * contagem no item do menu. Com o modal fechado quem relê é o header
 * (`overviewStale`): o modal não deixa leitura correndo depois de fechar.
 */
@Component({
  selector: 'app-account-members-modal',
  templateUrl: './account-members-modal.component.html',
  styleUrls: ['./account-members-modal.component.scss'],
})
export class AccountMembersModalComponent implements OnInit, OnDestroy {

  @Input() showModal = new Subject<void>();

  @Output() overviewLoaded = new EventEmitter<AccountMembersOverview>();

  /**
   * A contagem do header pode ter ficado para trás: o modal fechou com uma
   * leitura em voo (cancelada) ou uma ação terminou com ele já fechado. O
   * header relê com a própria consulta.
   */
  @Output() overviewStale = new EventEmitter<void>();

  @ViewChild('membersModal') membersModal: any;
  @ViewChild('inviteModal') inviteModal: any;

  overview: AccountMembersOverview = null;
  loading = false;
  loadFailed = false;

  inviteForm: UntypedFormGroup;
  inviting = false;
  inviteError: string = null;

  /**
   * Linhas com reenvio ou remoção em andamento: cada uma trava só os próprios
   * botões, e o fim de uma não destrava a outra.
   */
  busyMemberIds = new Set<string>();

  private inviteRef: NgbModalRef;

  /** O modal da lista, enquanto aberto. */
  private membersRef: NgbModalRef;
  private subscriptions = new Subscription();

  /**
   * A leitura da lista em voo. Uma por vez: a anterior é cancelada ao reler e
   * ao fechar o modal, senão a resposta velha chegando por último
   * sobrescreveria a lista (e a contagem do header) com quem já saiu.
   */
  private loadSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private modalService: NgbModal,
    private accountMemberService: AccountMemberService,
    private accountVersionService: AccountVersionService,
    private confirmationService: ConfirmationService,
    private toast: ToastService
  ) { }

  get limitReached(): boolean {
    return !!this.overview && this.overview.used >= this.overview.limit;
  }

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(45)]],
    });

    this.subscriptions.add(this.showModal.subscribe(() => this.open()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.loadSubscription?.unsubscribe();
  }

  open(): void {
    // A janela do `NgbModal` fica no `body`, fora da classe de versão do shell:
    // sem levar a classe junto, o botão primário fica verde no internacional.
    const ref = this.modalService.open(this.membersModal, {
      size: 'xl',
      centered: true,
      ariaLabelledBy: 'account-members-title',
      windowClass: this.accountVersionService.themeClass,
    });
    this.membersRef = ref;

    const onClose = () => {
      if (this.membersRef === ref) {
        this.membersRef = null;
      }

      this.stopLoading();
    };
    ref.closed.subscribe(onClose);
    ref.dismissed.subscribe(onClose);

    this.load();
  }

  load(): void {
    this.loadSubscription?.unsubscribe();
    this.loading = true;
    this.loadFailed = false;

    this.loadSubscription = this.accountMemberService
      .overview()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: overview => {
          this.overview = overview;
          this.overviewLoaded.emit(overview);
        },
        // Com a lista já na tela (reabertura, releitura depois de uma ação)
        // o estado de erro não aparece: avisa que ela pode estar velha. A
        // leitura marca `OWN_ERROR_TOAST`, então este é o único aviso — menos
        // no 401, que é do interceptor.
        error: (error: HttpErrorResponse) => {
          this.loadFailed = true;

          if (this.overview) {
            toastOwnError(this.toast, error?.status === 401 ? null : 'Não foi possível atualizar a lista de membros.');
          }
        },
      });
  }

  /**
   * Depois de convidar, reenviar ou remover. Com o modal aberto ele relê e a
   * lista nova vai ao header; fechado, só avisa o header.
   */
  private reload(): void {
    if (this.membersRef) {
      this.load();
    } else {
      this.overviewStale.emit();
    }
  }

  /** Fechar no meio de uma leitura: cancela e passa a releitura ao header. */
  private stopLoading(): void {
    if (this.loadSubscription && !this.loadSubscription.closed) {
      this.loadSubscription.unsubscribe();
      this.overviewStale.emit();
    }
  }

  openInvite(): void {
    this.inviteForm.reset({ name: '', email: '' });
    this.inviteError = null;

    this.inviteRef = this.modalService.open(this.inviteModal, {
      size: 'md',
      centered: true,
      ariaLabelledBy: 'account-invite-title',
      windowClass: this.accountVersionService.themeClass,
    });
  }

  submitInvite(): void {
    if (this.inviteForm.invalid || this.inviting) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    const { name, email } = this.inviteForm.value;
    this.inviting = true;
    this.inviteError = null;

    this.subscriptions.add(
      this.accountMemberService
        .invite(name.trim(), email.trim())
        .pipe(finalize(() => this.inviting = false))
        .subscribe({
          next: () => {
            this.toast.success(`Convite enviado para ${email.trim()}.`);
            this.inviteRef?.close();
            this.reload();
          },
          // E-mail já cadastrado e limite atingido ficam no próprio modal, ao
          // lado do campo que o dono vai corrigir.
          // No 401 o interceptor já avisou: nada a mostrar no modal.
          error: (error: HttpErrorResponse) => this.inviteError = accountMemberErrorMessage(error),
        })
    );
  }

  resend(member: AccountMember): void {
    if (this.busyMemberIds.has(member.id)) {
      return;
    }

    this.busyMemberIds.add(member.id);

    this.subscriptions.add(
      this.accountMemberService
        .resend(member.id)
        .pipe(finalize(() => this.busyMemberIds.delete(member.id)))
        .subscribe({
          next: () => {
            this.toast.success(`Convite reenviado para ${member.email}.`);
            this.reload();
          },
          error: (error: HttpErrorResponse) => toastOwnError(this.toast, accountMemberErrorMessage(error)),
        })
    );
  }

  confirmRemove(member: AccountMember): void {
    const message = member.status === 'active'
      ? `Remover ${member.name} da equipe? O acesso dele à conta é encerrado na hora.`
      : `Cancelar o convite de ${member.name}? O link enviado deixa de funcionar.`;

    this.confirmationService.show(message, () => this.remove(member));
  }

  statusLabel(member: AccountMember): string {
    return { active: 'Ativo', pending: 'Pendente', expired: 'Expirado' }[member.status];
  }

  /**
   * Coluna "Último acesso". Para convite, o que importa é o prazo: quanto falta
   * para expirar, ou há quanto tempo expirou.
   */
  accessLabel(member: AccountMember, now: Date = new Date()): string {
    if (member.status === 'pending') {
      return member.invitation_expires_at
        ? `Convite enviado · expira em ${durationLabel(new Date(member.invitation_expires_at).getTime() - now.getTime())}`
        : 'Convite enviado';
    }

    if (member.status === 'expired') {
      return member.invitation_expires_at
        ? `Expirou há ${durationLabel(now.getTime() - new Date(member.invitation_expires_at).getTime())}`
        : 'Convite expirado';
    }

    return member.last_access_at ? lastAccessLabel(new Date(member.last_access_at), now) : 'Ainda não acessou';
  }

  initial(member: AccountMember): string {
    return (member.name || member.email || '?').trim().charAt(0).toUpperCase();
  }

  trackById(_: number, member: AccountMember): string {
    return member.id;
  }

  private remove(member: AccountMember): void {
    // A confirmação aberta duas vezes não manda um segundo DELETE (que daria 404).
    if (this.busyMemberIds.has(member.id)) {
      return;
    }

    this.busyMemberIds.add(member.id);

    this.subscriptions.add(
      this.accountMemberService
        .remove(member.id)
        .pipe(finalize(() => this.busyMemberIds.delete(member.id)))
        .subscribe({
          next: () => {
            this.toast.success(member.status === 'active' ? 'Membro removido.' : 'Convite cancelado.');
            this.reload();
          },
          error: (error: HttpErrorResponse) => toastOwnError(this.toast, accountMemberErrorMessage(error)),
        })
    );
  }
}

/**
 * "31 h" até 48 h — a validade do convite, então o prazo de um pendente sempre
 * sai em horas —; "3 dias" daí para cima. Nunca menos de 1 h.
 */
function durationLabel(ms: number): string {
  if (ms < 2 * DAY_MS) {
    return `${Math.max(1, Math.round(ms / HOUR_MS))} h`;
  }

  const days = Math.round(ms / DAY_MS);
  return days === 1 ? '1 dia' : `${days} dias`;
}

function lastAccessLabel(date: Date, now: Date): string {
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  if (date.getTime() >= startOfToday) {
    return `Hoje às ${time}`;
  }

  if (date.getTime() >= startOfToday - DAY_MS) {
    return `Ontem às ${time}`;
  }

  return `${date.toLocaleDateString('pt-BR')} às ${time}`;
}
